import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SharedFilters from '../../../components/SharedFilters'
import AppActionMenu from '../../../components/AppActionMenu'
import AppPagination from '../../../components/AppPagination'
import PredefinedReportsDrawer from '../../../components/PredefinedReportsDrawer'
import { appToast } from '../../../components/AppToast'
import { useTestListScreen } from './hooks/useTestListScreen'
import TestNotificationModal from '../components/TestNotificationModal'
import TestAllocationModal from '../components/TestAllocationModal'
import TestReportCardModal from '../components/TestReportCardModal'

function formatTestDate(dateStr) {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const toOptions = (arr = []) =>
  arr.map((item) =>
    typeof item === 'object' ? item : { value: item, label: item },
  )

export default function TestListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useTestListScreen()

  // Modals state
  const [allocationTest, setAllocationTest] = useState(null)
  const [notificationModal, setNotificationModal] = useState({ isOpen: false, test: null, type: 'sms' })
  const [reportCardModal, setReportCardModal] = useState({ isOpen: false, initialMode: 'print' })

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const handleAction = (key, test) => {
    if (key === 'edit') navigate(`/education/tests/entry?id=${test.id}`)
    else if (key === 'delete') actions.handleDelete(test.id, test.testNo || test.topic)
    else if (key === 'questions') navigate(`/education/tests/question-paper?id=${test.id}`)
    else if (key === 'solution') navigate(`/education/tests/solution?id=${test.id}`)
    else if (key === 'allocation') setAllocationTest(test)
    else if (key === 'marks') navigate(`/education/tests/marks?id=${test.id}`)
    else if (key === 'sms') setNotificationModal({ isOpen: true, test, type: 'sms' })
    else if (key === 'whatsapp') setNotificationModal({ isOpen: true, test, type: 'whatsapp' })
    else if (key === 'statistics') navigate(`/education/tests/statistics?id=${test.id}`)
  }

  // Exactly matches legacy Tools dropdown (Image 1)
  const toolsConfig = [
    { key: 'print-report', label: 'Print Report', icon: 'print' },
    { key: 'report-card', label: 'ReportCard', icon: 'description' },
    { key: 'send-report-email', label: 'Send ReportCard Email', icon: 'description' },
    { key: 'send-report-template', label: 'Send ReportCard Template', icon: 'drafts' },
  ]

  const handleToolSelect = (key) => {
    if (key === 'print-report') actions.setIsReportsOpen(true)
    else if (key === 'report-card') setReportCardModal({ isOpen: true, initialMode: 'print' })
    else if (key === 'send-report-email') setReportCardModal({ isOpen: true, initialMode: 'email' })
    else if (key === 'send-report-template') navigate('/education/tests/report-card-settings')
  }

  // Exactly matches legacy 9 row actions in order (Image 2)
  const rowMenuItems = [
    { key: 'edit', label: 'Edit', icon: 'edit' },
    { key: 'delete', label: 'Delete', icon: 'close', danger: true, iconClassName: 'text-error' },
    { key: 'questions', label: 'Question Paper', icon: 'help_outline' },
    { key: 'solution', label: 'Solution', icon: 'check_circle', iconClassName: 'text-emerald-600' },
    { key: 'allocation', label: 'Allocation', icon: 'format_list_bulleted' },
    { key: 'marks', label: 'Marks', icon: 'bar_chart' },
    { key: 'sms', label: 'Send SMS', icon: 'chat_bubble' },
    { key: 'whatsapp', label: 'Send WhatsApp', icon: 'chat', iconClassName: 'text-emerald-600' },
    { key: 'statistics', label: 'Statistics', icon: 'pie_chart' },
  ]

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">

      {/* ── Page Header ─────────────────────────────────────────── */}
      <section className="flex flex-col gap-1">
        <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
          Tests Management
        </h1>
        <p className="mt-1 text-xs font-medium text-on-surface-variant">
          Search, filter, and manage examination schedules, roster evaluation, and marks.
        </p>
      </section>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        searchFieldOptions={[
          { value: 'testName', label: 'Name' },
          { value: 'topic', label: 'Topic' },
          { value: 'subject', label: 'Subject' },
          { value: 'testNo', label: 'Test No.' },
          { value: 'course', label: 'Course' },
          { value: 'batch', label: 'Batch' },
        ]}
        showStatus={false}
        showAccordionToggle={true}
        visibleAccordionFilters={[
          'company', 'branch', 'course', 'batch',
          'academicYear', 'datePeriod', 'fromDate', 'toDate',
          'subject', 'group', 'department',
        ]}
        companyFilter={state.company}
        branchFilter={state.branch}
        courseFilter={state.course}
        batchFilter={state.batch}
        academicYearFilter={state.academicYear}
        datePeriodFilter={state.datePeriod}
        fromDate={state.fromDate}
        toDate={state.toDate}
        subjectFilter={state.subject}
        groupFilter={state.group}
        departmentFilter={state.department}
        setCompanyFilter={actions.setCompany}
        setBranchFilter={actions.setBranch}
        setCourseFilter={actions.setCourse}
        setBatchFilter={actions.setBatch}
        setAcademicYearFilter={actions.setAcademicYear}
        setDatePeriodFilter={actions.handleDatePeriodChange}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        setSubjectFilter={actions.setSubject}
        setGroupFilter={actions.setGroup}
        setDepartmentFilter={actions.setDepartment}
        companyOptions={toOptions(state.options.companyOptions)}
        branchOptions={toOptions(state.options.branchOptions)}
        courseOptions={toOptions(state.options.courseOptions)}
        batchOptions={toOptions(state.options.batchOptions)}
        academicYearOptions={toOptions(state.options.academicYearOptions)}
        datePeriodOptions={toOptions(state.options.datePeriodOptions)}
        subjectOptions={toOptions(state.options.subjectOptions)}
        groupOptions={toOptions(state.options.groupOptions)}
        departmentOptions={toOptions(state.options.departmentOptions)}
        tools={toolsConfig}
        onToolSelect={handleToolSelect}
        onSearch={actions.search}
        onReset={actions.resetFilters}
      />

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {state.error && (
        <div className="rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">
          {state.error}
        </div>
      )}

      {/* ── Test Table ─────────────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm flex flex-col">
        {/* Column headers */}
        <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)_110px_60px] items-center border-b border-outline-variant/25 bg-surface-container-low/60 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div>Date / Test No</div>
          <div>Company / Course</div>
          <div>Branch / Batch</div>
          <div>Topic / Subject</div>
          <div>Min / Max</div>
          <div className="text-right">Actions</div>
        </div>

        {/* Data rows */}
        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading tests...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <span className="material-symbols-outlined mb-3 block text-5xl text-on-surface-variant/40">inbox</span>
              <p className="text-sm font-medium text-on-surface-variant">No tests match the current filters.</p>
              <button
                type="button"
                onClick={actions.resetFilters}
                className="mt-4 rounded-xl bg-primary/10 px-5 py-2 text-sm font-bold text-primary hover:bg-primary/20 cursor-pointer"
              >
                Show All Tests
              </button>
            </div>
          ) : (
            state.rows.map((test, rowIdx) => (
              <div
                key={test.id}
                className={`group grid grid-cols-[minmax(0,1.2fr)_minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1.1fr)_110px_60px] items-center border-b border-outline-variant/10 px-8 py-4 transition-colors hover:bg-surface-container-low/40 last:border-b-0 cursor-pointer ${
                  rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50'
                }`}
                onClick={() => navigate(`/education/tests/entry?id=${test.id}`)}
              >
                {/* Col 1: Date + Test No */}
                <div className="min-w-0 pr-4">
                  <p className="text-sm font-semibold text-on-surface group-hover:text-primary transition-colors leading-snug">
                    {formatTestDate(test.testDate)}
                  </p>
                  <p className="text-xs text-on-surface-variant leading-snug mt-0.5">#{test.testNo}</p>
                </div>

                {/* Col 2: Company + Course */}
                <div className="min-w-0 pr-4">
                  <p className="text-sm font-semibold text-on-surface leading-snug truncate">
                    {test.company || test.institute || test.instituteName || 'All Companies'}
                  </p>
                  <p className="text-xs text-on-surface-variant leading-snug truncate">
                    {test.course || test.courseName || '—'}
                  </p>
                </div>

                {/* Col 3: Branch + Batch */}
                <div className="min-w-0 pr-4">
                  <p className="text-sm text-on-surface leading-snug truncate">
                    {test.branch || test.branchNames || 'All Branches'}
                  </p>
                  <p className="text-xs text-on-surface-variant leading-snug truncate">
                    {test.batch || test.batchNames || 'All Batches'}
                  </p>
                </div>

                {/* Col 4: Topic + Subject */}
                <div className="min-w-0 pr-4">
                  <p className="text-sm text-on-surface leading-snug truncate">{test.topic || '—'}</p>
                  <p className="text-xs text-on-surface-variant leading-snug truncate">
                    {test.subject || test.subjectName || '—'}
                  </p>
                </div>

                {/* Col 5: Min + Max */}
                <div>
                  <p className="text-sm text-on-surface leading-snug">{test.minMarks ?? 0}</p>
                  <p className="text-xs text-on-surface-variant leading-snug">{test.maxMarks || test.totalMarks || 100}</p>
                </div>

                {/* Col 6: Actions */}
                <div className="flex justify-end select-none" onClick={(e) => e.stopPropagation()}>
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    menuClassName="min-w-[190px]"
                    items={rowMenuItems}
                    onSelect={(key) => handleAction(key, test)}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Pagination Footer ────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3 border-t border-outline-variant/30 bg-surface-container-low/30 px-8 py-5 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-on-surface-variant">
            Showing {state.rows.length === 0 ? 0 : (state.currentPage - 1) * state.pageSize + 1}–{Math.min(state.currentPage * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </span>
          {state.totalPages > 1 && (
            <AppPagination
              page={state.currentPage}
              totalPages={state.totalPages}
              canPrevious={state.currentPage > 1}
              canNext={state.currentPage < state.totalPages}
              onPrevious={() => actions.setCurrentPage(Math.max(1, state.currentPage - 1))}
              onNext={() => actions.setCurrentPage(Math.min(state.totalPages, state.currentPage + 1))}
              onPageChange={(p) => actions.setCurrentPage(p)}
            />
          )}
        </div>
      </div>

      {/* ── Predefined Reports Drawer ────────────────────────────────────── */}
      <PredefinedReportsDrawer
        isOpen={state.isReportsOpen}
        onClose={() => actions.setIsReportsOpen(false)}
        title="Test Reports"
        module="education"
        submodule="tests"
        filters={{
          academicYear: state.academicYear,
          companyId: state.company,
          branchId: state.branch,
          courseId: state.course,
          batchId: state.batch,
          subjectId: state.subject,
          groupId: state.group,
          department: state.department,
          fromDate: state.fromDate,
          toDate: state.toDate,
          searchField: state.searchField,
          searchText: state.searchText,
        }}
      />

      {/* ── Modals ───────────────────────────────────────────────────────── */}
      <TestAllocationModal
        isOpen={Boolean(allocationTest)}
        test={allocationTest}
        onClose={() => setAllocationTest(null)}
        onSuccess={() => actions.search()}
      />

      <TestNotificationModal
        isOpen={notificationModal.isOpen}
        test={notificationModal.test}
        type={notificationModal.type}
        onClose={() => setNotificationModal({ isOpen: false, test: null, type: 'sms' })}
      />

      <TestReportCardModal
        isOpen={reportCardModal.isOpen}
        initialMode={reportCardModal.initialMode}
        academicYearOptions={toOptions(state.options.academicYearOptions)}
        courseOptions={toOptions(state.options.courseOptions)}
        branchOptions={toOptions(state.options.branchOptions)}
        batchOptions={toOptions(state.options.batchOptions)}
        onClose={() => setReportCardModal({ isOpen: false, initialMode: 'print' })}
      />
    </div>
  )
}
